// @ts-check
import { useEffect, useReducer } from "react"
import { getAllProjects } from "../../../../services/project.service.js"
import {
  resetCategory,
  resetFields,
  resetModule,
  resetProject,
  setCategoriesError,
  setCategoriesLoading,
  setCategoriesOptions,
  setFetchedModules,
  setFetchedModulesError,
  setInitiallySelectedDates,
  setIsFetchingModules,
  setProjectsError,
  setProjectsLoading,
  setProjectsOptions,
  updateProject,
} from "./actions.js"
import { formReducer } from "./reducer.js"
import { groupBy } from "lodash"
import { getProjectCategories } from "../../../../services/category.service.js"
import { getProjectModules } from "../../../../services/module.service.js"
import useMount from "../../../../hooks/useMount.js"
import { DateTime } from "luxon"

/** @typedef {import('./actionsT.d.ts').UpdateProjectAction} UpdateProjectAction */

/**
 * @param {selectedTasks[]} tasks
 * @returns {[number, DateTime[]]}
 */
function getUniqueDates(tasks) {
  const seenDates = {}
  let uniqueDatesCount = 0

  for (const task of tasks) {
    const d = DateTime.fromISO(task.date, { zone: "utc" })
    const dateKey = `${d.day}/${d.month}/${d.year}`
    if (!seenDates[dateKey]) {
      seenDates[dateKey] = d.set({ hour: 12 })
      uniqueDatesCount++
    }
  }

  return [uniqueDatesCount, Object.values(seenDates)]
}

/**
 * @typedef {Object} selectedTasks
 * @property {number} taskId
 * @property {string} taskDayNumber
 * @property {string} date
 * @property {number} isVacationDay
 * @property {null} vacationsStartDate
 * @property {null} vacationsEndDate
 * @property {null} vacationsMotive
 * @property {number} isHoliday
 * @property {null} idTypeOfHoliday
 * @property {null} holidayMotive
 * @property {null} holidaySchemaName
 * @property {null} holidaySchemaType
 * @property {number} teamMemberHolidaySchemaId
 * @property {number} hasTasksInDate
 * @property {null} workDayComment
 * @property {string} clientName
 * @property {string} clientCode
 * @property {number} clientIsBillable
 * @property {number} clientId
 * @property {string} projectName
 * @property {string} projectCode
 * @property {number} projectId
 * @property {string|null} module
 * @property {string} category
 * @property {string} categoryTitle
 * @property {string} categorySubtitle
 * @property {number} categoryId
 * @property {string|null} specialCategoryTitle
 * @property {number|null} specialCategoryId
 * @property {string} task
 * @property {number} sisuTime
 * @property {number} sisuTimeModifiedAfterFinished
 * @property {number} billedTime
 * @property {number} billedTimeModifiedAfterFinished
 * @property {null} comment
 * @property {null} timeDifferenceComment
 * @property {number|null} moduleId
 * @property {number} timeDifference
 * @property {number} workDayId
 * @property {number} hasChangelog
 * @property {number} taskOrder
 * @property {string|null} budgetId
 * @property {number} isSubheader
 * @property {number} realBilledTime
 * @property {number} realTimeDifference
 */

/**
 * @param {selectedTasks[]} selectedTasks
 * @param {boolean} modalIsVisible
 */
export function useFormState(selectedTasks, modalIsVisible) {
  const [datesInitiallySelectedCount, uniqueDates] =
    getUniqueDates(selectedTasks)

  const initiallySelectedProjectsAllHaveSpecialCategories = selectedTasks.every(
    (el) => el.specialCategoryId !== null
  )

  /** @type {[import('./reducer.d.js').FormState, React.Dispatch<import('./actionsT.d.js').Action>]} */
  const [state, dispatch] = useReducer(formReducer, {
    status: "initial",
    fields: {
      project: undefined,
      selectedProjectUsesSpecialCategories:
        initiallySelectedProjectsAllHaveSpecialCategories,
      category: undefined,
      module: undefined,
      date: undefined,
      comment: "",
      shouldDeleteBilledTime: false,
      timeDifferenceComment: "",
    },

    initiallySelectedDates: uniqueDates,

    isFetchingProjects: false,
    availableProjects: [],
    projectsError: undefined,

    isFetchingCategories: false,
    availableCategories: [],
    categoriesError: undefined,

    isFetchingModules: false,
    availableModules: [],
    modulesError: undefined,
  })

  useEffect(() => {
    const [, uniqueDates] = getUniqueDates(selectedTasks)

    dispatch(setInitiallySelectedDates(uniqueDates))
    dispatch(resetFields(initiallySelectedProjectsAllHaveSpecialCategories))
  }, [selectedTasks])

  useMount(fetchProjects)

  /** @param {string} newProject */
  const changeSelectedProject = (newProject) => {
    const selectedProjectData = state.availableProjects
      .find((p) => p.items.find((i) => i.projectName === newProject))
      ?.items.find((i) => i.projectName === newProject)

    if (state.fields.project) {
      const currentProjectData = state.availableProjects
        .find((p) =>
          p.items.find((i) => i.projectName === state.fields.project)
        )
        ?.items.find((i) => i.projectName === state.fields.project)

      const bothProjectsHaveRegularCategories =
        currentProjectData?.hasSpecialCategory === 0 &&
        selectedProjectData?.hasSpecialCategory === 0

      if (!bothProjectsHaveRegularCategories && selectedProjectData) {
        fetchCategories(selectedProjectData.projectID)
        dispatch(resetCategory())
      }
    } else {
      fetchCategories(selectedProjectData?.projectID)
      dispatch(resetCategory())
    }

    let resettingProject = false

    if (hasOnlyOneProjectInitiallyAggregated) {
      const initialProject = selectedTasks[0].projectName
      const selectedInitialProject = newProject === initialProject

      if (selectedInitialProject) {
        resettingProject = true
      }
    }

    if (resettingProject) {
      dispatch(resetProject())
    } else {
      dispatch(
        updateProject(newProject, selectedProjectData?.hasSpecialCategory === 1)
      )
    }
  }

  const initialProjectsHaveMixedCategories =
    selectedTasks.some((task) => task.specialCategoryId) &&
    selectedTasks.some((task) => !task.specialCategoryId)

  const initiallySelectedProjectsAllHaveRegularCategories = selectedTasks.every(
    (el) => el.specialCategoryId === null
  )

  useEffect(() => {
    if (modalIsVisible) {
      if (hasOnlyOneProjectInitiallyAggregated) {
        fetchModules(selectedTasks[0].projectId)
        fetchCategories(selectedTasks[0].projectId)
      }

      if (!initialProjectsHaveMixedCategories) {
        fetchCategories(selectedTasks[0].projectId)
      }
    }
  }, [modalIsVisible])

  useEffect(() => {
    if (state.fields.project) {
      const currentProjectData = state.availableProjects
        .find((p) =>
          p.items.find((i) => i.projectName === state.fields.project)
        )
        ?.items.find((i) => i.projectName === state.fields.project)

      fetchModules(currentProjectData?.projectID)
    } else {
      const firstItem = selectedTasks[0]
      if (firstItem) {
        if (!initialProjectsHaveMixedCategories) {
          fetchCategories(firstItem?.projectId)
        }

        if (hasOnlyOneProjectInitiallyAggregated) {
          fetchModules(firstItem?.projectId)
        }
      }
    }
  }, [state.fields.project])

  async function fetchProjects() {
    dispatch(setProjectsLoading(true))
    try {
      const resp = await getAllProjects()
      dispatch(
        setProjectsOptions(
          Object.entries(groupBy(resp.result, (el) => el.clientTagName)).map(
            ([clientTagName, projects]) => {
              return {
                label: clientTagName,
                clientName: projects[0].clientName,
                items: projects,
              }
            }
          )
        )
      )
    } catch (err) {
      dispatch(setProjectsError(err.message))
    }
    dispatch(setProjectsLoading(false))
  }

  async function fetchModules(projectId) {
    dispatch(setIsFetchingModules(true))
    dispatch(resetModule())
    try {
      const resp = await getProjectModules(projectId)
      dispatch(setFetchedModules(resp.result))
    } catch (err) {
      dispatch(setFetchedModulesError(err.message))
    }
    dispatch(setIsFetchingModules(false))
  }

  async function fetchCategories(projectId) {
    dispatch(setCategoriesLoading(true))
    try {
      const resp = await getProjectCategories(projectId)
      dispatch(setCategoriesOptions(resp.result))
    } catch (err) {
      dispatch(setCategoriesError(err.message))
    }
    dispatch(setCategoriesLoading(false))
  }

  const hasOnlyOneProjectInitiallyAggregated =
    Object.keys(groupBy(selectedTasks, (el) => el.projectCode)).length === 1

  const hasOnlyIntProjectAggregated = (() => {
    if (state.fields.project) {
      const client = state.availableProjects.find((p) =>
        p.items.find((i) => i.projectName === state.fields.project)
      )
      return !client?.items[0].isBillable
    } else {
      return hasOnlyOneProjectInitiallyAggregated ?
          !selectedTasks[0].clientIsBillable
        : false
    }
  })()

  const hasIntInInitiallySelectedTasks = selectedTasks.some(
    (t) => t.clientIsBillable === 0
  )

  const isCategoryDisabled = (() => {
    if (state.isFetchingCategories) {
      return true
    }
    if (state.fields.project !== undefined) return false
    if (selectedTasks.length === 1) return false
    if (hasOnlyOneProjectInitiallyAggregated) return false

    if (initiallySelectedProjectsAllHaveRegularCategories) {
      return false
    }

    if (initiallySelectedProjectsAllHaveSpecialCategories) {
      return true
    }

    const initialProjectsCategoriesAreMixed =
      !initiallySelectedProjectsAllHaveRegularCategories &&
      !initiallySelectedProjectsAllHaveSpecialCategories

    return initialProjectsCategoriesAreMixed
  })()

  const selectedCategories = selectedTasks
    .map((t) => t.categoryTitle)
    .filter((t) => t !== null)
    .reduce(
      (acc, curr) => {
        if (!acc.find((v) => v === curr)) {
          acc.push(curr)
        }
        return acc
      },
      /** @type {string[]} */
      ([])
    )

  const selectedModules = selectedTasks
    .map((t) => t.module)
    .filter((t) => t !== null)
    .reduce(
      (acc, curr) => {
        if (!acc.find((v) => v === curr)) {
          acc.push(curr)
        }
        return acc
      },
      /** @type {string[]} */
      ([])
    )

  const selectedProjectHasNoModules = state.availableModules.length === 0

  const isModuleDropdownDisabled = (() => {
    if (state.isFetchingModules) return true
    if (selectedProjectHasNoModules) return true

    if (!state.fields.project) {
      return !hasOnlyOneProjectInitiallyAggregated
    } else {
      return false
    }
  })()

  const changesCanBeSaved = (() => {
    let res = false

    if (state.status !== "initial") {
      res = true
    }

    if (state.fields.project) {
      if (!state.fields.category) {
        res = false
      }

      if (!selectedProjectHasNoModules) {
        if (!state.fields.module) {
          res = false
        }
      }
    }

    if (state.fields.shouldDeleteBilledTime) {
      if (!state.fields.timeDifferenceComment) {
        res = false
      }
    }

    return res
  })()

  const initiallySelectedTasksHaveNoBilledTime = selectedTasks.every(
    (t) => t.billedTime === 0
  )

  return {
    state,
    dispatch,
    hasOnlyOneProjectInitiallyAggregated,
    hasOnlyIntProjectAggregated,
    hasIntInInitiallySelectedTasks,
    isCategoryDisabled,
    selectedCategories,
    selectedModules,
    changeSelectedProject,
    selectedProjectHasNoModules,
    isModuleDropdownDisabled,
    datesInitiallySelectedCount,
    changesCanBeSaved,
    initiallySelectedTasksHaveNoBilledTime,
  }
}
